import { Capacitor } from '@capacitor/core';
import { AdMob, RewardAdOptions, RewardAdPluginEvents, AdMobRewardItem, InterstitialAdPluginEvents } from '@capacitor-community/admob';

// AdMob Test IDs (use these for testing, replace with real IDs in production)
const TEST_IDS = {
  REWARDED: 'ca-app-pub-3940256099942544/5224354917', // Google test rewarded ad
  INTERSTITIAL: 'ca-app-pub-3940256099942544/1033173712', // Google test interstitial ad
};

// Production Ad Unit IDs (replace with your actual AdMob ad unit IDs)
// Use import.meta.env for Vite (not process.env)
const PRODUCTION_IDS = {
  REWARDED: import.meta.env.VITE_ADMOB_REWARDED_AD_ID || TEST_IDS.REWARDED,
  INTERSTITIAL: import.meta.env.VITE_ADMOB_INTERSTITIAL_AD_ID || TEST_IDS.INTERSTITIAL,
};

// Use test IDs in development, production IDs in production
// Vite: import.meta.env.MODE is 'development' or 'production'
const USE_TEST_ADS = (import.meta.env as any).MODE !== 'production';
const AD_IDS = USE_TEST_ADS ? TEST_IDS : PRODUCTION_IDS;

// Coin reward amounts (can be configured)
const REWARDED_AD_COINS = 50; // Coins awarded for watching a rewarded ad
const INTERSTITIAL_AD_COINS = 25; // Coins awarded for watching an interstitial ad

class AdService {
  private isInitialized = false;
  private isNative = false;

  constructor() {
    this.isNative = Capacitor.isNativePlatform();
  }

  /**
   * Initialize AdMob (only on native platforms)
   */
  async initialize(): Promise<void> {
    if (!this.isNative) {
      console.log('AdMob: Not available on web platform');
      return;
    }

    if (this.isInitialized) {
      return;
    }

    try {
      // Initialize AdMob
      await AdMob.initialize({
        requestTrackingAuthorization: true,
        testingDevices: [], // Add test device IDs here for testing
        initializeForTesting: USE_TEST_ADS,
      });

      this.isInitialized = true;
      console.log('AdMob initialized successfully');
    } catch (error) {
      console.error('Failed to initialize AdMob:', error);
      throw error;
    }
  }

  /**
   * Check if ads are available (only on native platforms)
   */
  isAvailable(): boolean {
    return this.isNative && this.isInitialized;
  }

  /**
   * Show a rewarded ad and return coins if watched successfully
   * Returns 0 if ad fails or on web platform
   */
  async showRewardedAd(): Promise<number> {
    if (!this.isNative) {
      console.log('AdMob: Rewarded ads not available on web platform');
      // For web, we could show a mock ad or return 0
      return 0;
    }

    if (!this.isInitialized) {
      await this.initialize();
    }

    return new Promise(async (resolve) => {
      const adId = AD_IDS.REWARDED;
      let rewarded = false;
      let listeners: Array<{ remove: () => void }> = [];

      // Cleanup function
      const cleanup = () => {
        listeners.forEach(listener => listener.remove());
        listeners = [];
      };

      try {
        // Set up reward handler
        const rewardedListener = await AdMob.addListener(RewardAdPluginEvents.Rewarded, (reward: AdMobRewardItem) => {
          console.log('Rewarded ad completed:', reward);
          rewarded = true;
          resolve(REWARDED_AD_COINS);
          cleanup();
        });
        listeners.push(rewardedListener);

        // Set up dismissed handler (user closed ad without completing)
        const dismissedListener = await AdMob.addListener(RewardAdPluginEvents.Dismissed, () => {
          console.log('Rewarded ad dismissed');
          if (!rewarded) {
            resolve(0);
          }
          cleanup();
        });
        listeners.push(dismissedListener);

        // Set up failed handler
        const failedListener = await AdMob.addListener(RewardAdPluginEvents.FailedToLoad, (error) => {
          console.error('Rewarded ad failed to load:', error);
          resolve(0);
          cleanup();
        });
        listeners.push(failedListener);

        // Prepare and show the ad
        const options: RewardAdOptions = {
          adId: adId,
        };

        await AdMob.prepareRewardVideoAd(options);
        await AdMob.showRewardVideoAd();
      } catch (error) {
        console.error('Error showing rewarded ad:', error);
        resolve(0);
        cleanup();
      }
    });
  }

  /**
   * Show an interstitial ad and return coins if watched successfully
   * Returns 0 if ad fails or on web platform
   */
  async showInterstitialAd(): Promise<number> {
    if (!this.isNative) {
      console.log('AdMob: Interstitial ads not available on web platform');
      return 0;
    }

    if (!this.isInitialized) {
      await this.initialize();
    }

    return new Promise(async (resolve) => {
      const adId = AD_IDS.INTERSTITIAL;
      let rewarded = false;

      // Set up dismissed handler (user closed ad)
      const dismissedListener = await AdMob.addListener(InterstitialAdPluginEvents.Dismissed, () => {
        console.log('Interstitial ad dismissed');
        if (!rewarded) {
          rewarded = true;
          resolve(INTERSTITIAL_AD_COINS);
        }
        dismissedListener.remove();
        failedListener.remove();
      });

      // Set up failed handler
      const failedListener = await AdMob.addListener(InterstitialAdPluginEvents.FailedToLoad, (error) => {
        console.error('Interstitial ad failed to load:', error);
        resolve(0);
        dismissedListener.remove();
        failedListener.remove();
      });

      try {
        // Prepare and show the interstitial ad
        await AdMob.prepareInterstitial({ adId: adId });
        await AdMob.showInterstitial();
        
        // Interstitial ads are rewarded when dismissed (user viewed the ad)
        // The listener will handle the reward
      } catch (error) {
        console.error('Error showing interstitial ad:', error);
        resolve(0);
        dismissedListener.remove();
        failedListener.remove();
      }
    });
  }

  /**
   * Preload ads (optional, for better UX)
   */
  async preloadRewardedAd(): Promise<void> {
    if (!this.isNative || !this.isInitialized) return;

    try {
      await AdMob.prepareRewardVideoAd({ adId: AD_IDS.REWARDED });
      console.log('Rewarded ad preloaded');
    } catch (error) {
      console.error('Failed to preload rewarded ad:', error);
    }
  }
}

// Export singleton instance
export const adService = new AdService();

